import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { OptimizedImage } from '@/components/OptimizedImage';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { prefetchPlaceDetail } from '@/hooks/usePlaceDetailQuery';

interface Place {
  Name: string;
  City: string;
  Country: string;
  Image: string | null;
  Latitude: number;
  Longitude: number;
}

interface NearbyPlacesProps {
  currentPlace: {
    Name: string;
    Latitude: number;
    Longitude: number;
  };
  className?: string;
}

// Calcul de la distance en km entre deux points (formule de Haversine)
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Rayon de la Terre en km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const formatDistance = (distance: number): string => {
  if (distance < 1) {
    return `${Math.round(distance * 1000)}m`;
  }
  return `${distance.toFixed(1)}km`;
};

export const NearbyPlaces: React.FC<NearbyPlacesProps> = ({ currentPlace, className = '' }) => {
  const [nearbyPlaces, setNearbyPlaces] = useState<(Place & { distance: number })[]>([]);
  const [loading, setLoading] = useState(true);
  const { language } = useTranslation();
  const heading = language === 'FR' ? 'À proximité' : 'Nearby';

  useEffect(() => {
    const fetchNearbyPlaces = async () => {
      try {
        // Récupérer tous les lieux sauf le lieu actuel
        const { data: places, error } = await supabase
          .from('podcasts')
          .select('Name, City, Country, Image, Latitude, Longitude')
          .not('Name', 'eq', currentPlace.Name)
          .not('Latitude', 'is', null)
          .not('Longitude', 'is', null);

        if (error) throw error;

        if (places) {
          // Calculer les distances et trier
          const placesWithDistance = places
            .map(place => ({
              ...place,
              distance: calculateDistance(
                currentPlace.Latitude,
                currentPlace.Longitude,
                place.Latitude,
                place.Longitude
              )
            }))
            .sort((a, b) => a.distance - b.distance)
            .slice(0, 2); // Garder seulement les 2 plus proches

          setNearbyPlaces(placesWithDistance);
        }
      } catch (error) {
        console.error('Error fetching nearby places:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentPlace.Latitude && currentPlace.Longitude) {
      fetchNearbyPlaces();
    }
  }, [currentPlace]);

  if (loading) {
    return (
      <section className={cn('bg-card rounded-2xl border border-border/50 p-5', className)}>
        <h3 className="text-base font-semibold mb-4 tracking-tight">{heading}</h3>
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="animate-pulse flex gap-3">
              <div className="w-14 h-14 flex-shrink-0 bg-muted rounded-lg" />
              <div className="flex-1 space-y-2 py-1">
                <div className="h-3 bg-muted rounded w-3/4" />
                <div className="h-2 bg-muted rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (nearbyPlaces.length === 0) {
    return null;
  }

  return (
    <section className={cn('bg-card rounded-2xl border border-border/50 p-5', className)}>
      <h3 className="text-base font-semibold mb-4 tracking-tight">{heading}</h3>
      <div className="space-y-1">
        {nearbyPlaces.map((place) => (
          <Link
            key={place.Name}
            to={`/place/${encodeURIComponent(place.Name)}`}
            className="block group"
            onPointerEnter={() => prefetchPlaceDetail(place.Name)}
          >
            <div className="flex gap-3 p-2 -mx-2 rounded-lg transition-colors duration-200 hover:bg-muted/50">
              {/* Image */}
              <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-muted">
                {place.Image ? (
                  <OptimizedImage
                    src={place.Image}
                    alt={`${place.Name}, ${place.City}`}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-200"
                    width={56}
                    height={56}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-earth flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-white/80" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 py-0.5">
                <h4 className="font-medium text-sm text-foreground group-hover:text-primary transition-colors truncate leading-tight">
                  {place.Name}
                </h4>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {place.City}, {place.Country}
                </p>
                <p className="text-[11px] text-primary font-semibold mt-0.5 tabular-nums">
                  {formatDistance(place.distance)}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};